import type { RunCompleteness } from '~~/shared/workflows/completeness'
import type { ChatToolCall, JsonObject, JsonValue, QuickResearchActivityStep, ToolCallOutput } from '~~/shared/toolCalls'
import type { ItemRow } from '~~/shared/items/itemRow'
import type { ToolActivityKind } from '~~/shared/web-chat/toolActivity'
import type { ToolId, ToolSourceKind } from '#shared/tools/tool'
import type { QueuedChatProgress } from '~~/shared/chat/queuedChatTurn'

/**
 * A document attached to a chat message. Projected from `ItemRow` rather than re-declared — an
 * attachment IS an item, and a hand-copy of three of its columns could only drift from them.
 */
export type MessageAttachment = Pick<ItemRow, 'id' | 'name' | 'file_name'>

/** An image attached to a chat message (vision). `data` is base64 (no data: prefix). */
export interface ChatImage {
  mimeType: string
  data: string
  /** Original filename, for the UI thumbnail label. */
  name?: string
}

/**
 * One hit in the agent's document-search card. The item fields are projected from `ItemRow`;
 * `contract_type` is OPTIONAL here (unlike on the row) because a search result assembled by a tool
 * need not carry it, and `snippet` is the card's own — no column holds it.
 */
export type SearchResultDoc =
  Pick<ItemRow, 'id' | 'name' | 'file_name'>
  & { contract_type?: ItemRow['contract_type'], snippet?: string | null }

export interface SearchResultsCard {
  query?: string
  intro?: string
  docs: SearchResultDoc[]
}

/**
 * A card rendered in the message thread by a confirm client tool, discriminated by the tool that
 * proposed it. One shape, so `<ChatToolCard>` can resolve the component and `ChatPanel` can settle
 * any card through one accept/decline path.
 *
 * `callId` is the paused ADK function-call id: present when the `tool` wire event carried one, and
 * what `resolveConfirmToolCall` echoes back to resume the session. `toolName` differs from `toolId`
 * only for the GENERATED per-workflow tools (`run_workflow__<slug>`, MAR-97), which share one card
 * but must resume under the name the model actually called.
 */
interface CardBase {
  callId?: string
  toolName?: string
}

export type MessageCard =
  | (CardBase & { toolId: 'delete_files'; request: DeleteConfirmRequest })
  | (CardBase & { toolId: 'move_files'; request: MoveConfirmRequest })
  | (CardBase & { toolId: 'rename_files'; request: RenameConfirmRequest })
  | (CardBase & { toolId: 'create_folder'; request: CreateFolderRequest })
  | (CardBase & { toolId: 'file_to_case'; request: FileToCaseRequest })
  | (CardBase & { toolId: 'clear_chat_scope'; request: ClearScopeRequest })
  | (CardBase & { toolId: 'present_document_results'; request: SearchResultsCard })
  | (CardBase & { toolId: 'download_documents'; request: DownloadDocumentsRequest })
  | (CardBase & { toolId: 'run_workflow'; request: WorkflowRequest })
  | (CardBase & { toolId: 'convert_document'; request: ConvertDocumentRequest })

/** Every card id, for exhaustive component/handler maps. */
export type MessageCardId = MessageCard['toolId']

export interface WebChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  apiContent?: string
  hiddenFromUi?: boolean
  segments?: { content: string }[]
  attachments?: MessageAttachment[]
  /** Vision: images attached to this (user) turn. Sent to /api/chat as the top-level `images` field. */
  images?: ChatImage[]
  /**
   * The ONE card this assistant turn carries, if any. Was eight parallel optional fields plus a
   * separate `pendingClientToolCall`; the resume bookkeeping is now part of the card itself, so a
   * card and its paused tool call cannot get out of step.
   */
  card?: MessageCard
  /**
   * Result lists (`present_document_results`) this turn showed BEFORE `card`, oldest first
   * (MAR-1017). A result list has nothing to answer — its session resumed with an ack the moment it
   * rendered — so a later card in the same turn must not erase it: asked for two lists, the agent
   * presents twice, and the first one used to vanish. Render through `messageCards`.
   */
  earlierCards?: MessageCard[]
  validation?: AnswerGroundingValidationState
  toolCalls?: WebChatMessageToolCall[]
  /**
   * The turn was stopped because the free plan's Fast (chat) allowance is spent (MAR-1390 round 3):
   * the message offers a button that switches the selector to Slow. Display only, never persisted —
   * a reload shows the free-plan sentence without the button.
   */
  offerSlowTier?: boolean
  /**
   * A queued Slow (Gemma) turn's progress while it runs (MAR-1424): its stage, its place in line, and
   * whether the user asked for it by email. Read off the job doc; display only, dropped when the turn
   * ends, never persisted.
   */
  queuedTurn?: QueuedTurnView
  timestamp: number
}

/** `QueuedChatProgress` plus the in-flight state of the email button. */
export type QueuedTurnView = QueuedChatProgress & { emailPending?: boolean }

export interface WebChatMessageToolCall {
  id?: string | number
  callId?: string
  toolCallRowId?: string
  name: string
  input: JsonValue
  output?: ToolCallOutput | string | null
  activity?: ChatToolCall['activity']
  /** Live-streamed ordered quick-research steps (search/read), from the `tool_result` wire event. */
  activitySteps?: QuickResearchActivityStep[]
  subagentSteps?: string[]
  status?: ChatToolCall['status']
  segmentIndex: number
  startedAt?: string
  completedAt?: string | null
}

export interface AnswerGroundingValidationState {
  status: 'not_applicable' | 'running' | 'passed' | 'warning' | 'unavailable'
  checkedAt?: string
  summary?: string
  checkedClaims?: AnswerGroundingValidationClaim[]
  failedClaims?: AnswerGroundingValidationClaim[]
  claims?: AnswerGroundingValidationClaim[]
}

export interface AnswerGroundingValidationClaim {
  claim: string
  citations: string[]
  citationLabels?: string[]
  verdict: 'supported' | 'unsupported' | 'unclear' | 'missing_citation' | 'not_legal_claim'
  reason: string
  /** Deterministic tier from `verdict` + `claimType`, computed server-side — see
   * `server/services/chat/answerGroundingValidator.ts`'s `computeClaimSeverity`. */
  severity: 'high' | 'medium' | 'low' | 'none'
}

/** One field the card renders, resolved against the workflow's own declared inputs. */
export interface WorkflowRequestField {
  key: string
  label: string
  type: string
  required: boolean
  options?: { value: string; label: string }[]
  /**
   * The input's declared `constraints`, verbatim from the definition.
   *
   * Carried whole rather than cherry-picked. `options` above is the one field that predates this and
   * stays for the callers already reading it; everything else the run form's controls need —
   * `entitySource` above all, which is what makes the client's stored DUI / domicilio / nacionalidad
   * offerable on the card instead of guessed by the model — arrives here. Dropping `constraints`
   * during the mapping is exactly why the card could not autofill an entity: the hint never reached
   * it.
   */
  constraints?: Record<string, any>
  /** The current value — prefilled by the agent, then edited by the user. */
  value: unknown
  /** True when the agent supplied this value, so the card can show what it inferred vs. asked for. */
  prefilled: boolean
}

/**
 * A proposed workflow run (MAR-437). Two phases, one card.
 *
 * PHASE 1 — proposal. The agent has filled what the conversation established; `fields` carries the
 * workflow's REAL inputs with those values in place, and the user completes the rest. Nothing runs
 * until they confirm, which is why an incomplete prefill is fine: unlike MAR-97's generated tools,
 * a missing required input is a form to finish rather than a tool-call error.
 *
 * The card showing the actual fields is also what answers MAR-38 — a user approving a run had
 * nothing on screen that could contradict what they asked for. Now every value going in is visible
 * and editable.
 *
 * PHASE 2 — status. Once `runId` is set the card tracks the run through the Activity Center store
 * rather than a poller of its own.
 */
/**
 * A proposed file conversion (MAR-476) — one document in, one Word document out.
 *
 * Deliberately NOT a `WorkflowRequest`. A converter is not a workflow underneath any more either:
 * MAR-487 made the two of them code-only tools, which is why `toolId` below is a `ToolId` from
 * `shared/tools/tool.ts` and not a `workflow_definitions` uuid. The separation predates that and
 * holds for its own reason — `WorkflowRequest` carries `fields`, coerced values and a live draft
 * document because its card negotiates a FORM; a conversion takes one document and nothing else,
 * so all of that would be dead weight whose only effect is firing a vision call on every edit.
 *
 * The two names are resolved from the app, never taken from the model — same rule as
 * `ClearScopeRequest`'s `scopeLabel`. The user is about to act on this card, and a hallucinated
 * filename on it is a click on the wrong document.
 */
export interface ConvertDocumentRequest {
  toolId: ToolId
  documentId: string
  /**
   * The tool's Spanish label, from `TOOL_PRESENTATION`. Known synchronously now that the roster is
   * code — under MAR-476 it had to be fetched, which is what `loading` and the blank string were for.
   */
  toolLabel: string
  /** Still resolved from the app, never from the model: a hallucinated filename is a wrong click. */
  documentName: string
  /** What the tool reads, for the card's own mismatch warning. */
  source: ToolSourceKind
  /**
   * The source file's own format (`PDF`, `JPG`, …), from its file name once the document resolves.
   * The card states it (MAR-991); until it is known the card names the tool's source kind instead.
   */
  sourceFormat?: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'
  loading: boolean
  /**
   * ALREADY LOCALIZED — the card renders this verbatim, so whatever writes it owes the translation.
   *
   * Never a server `message`. `ToolRunError.message` is an operator diagnostic that names the
   * internal tool id and is English regardless of the reader's language; assigning it here is what
   * put `image_to_word reads only image files, and "bonilla-scanned" is not one.` in front of a
   * Spanish-speaking user (MAR-491). Derive it from `ToolRunError.code` via `toolRunFailureText`.
   */
  error?: string | null
  /** Set once the run is created, so the card can show live status from the runs store. */
  runId?: string | null
  /**
   * The Word document the run produced, once it succeeded — what the finished card links to
   * (MAR-991). Null on a failed run, and if that document was later deleted.
   */
  outputItemId?: string | null
  outputItemName?: string | null
}

export interface WorkflowRequest {
  workflowId: string
  workflowName: string
  reason: string
  status: 'pending' | 'running' | 'completed' | 'failed' | 'declined'
  /** Resolved from the workflow definition after the card renders; empty until then. */
  fields: WorkflowRequestField[]
  /** True while the definition is being fetched — the card renders immediately, fields fill in. */
  loading: boolean
  /**
   * Authored warnings on the template, verbatim. A `blocking` one is the template saying it does not
   * apply — the employment contract's says it covers *tiempo indefinido* only — so the card shows
   * them before the user commits rather than after the document is wrong.
   */
  notices?: { severity: 'blocking' | 'warning'; message: string }[]
  /**
   * Set when the run starts (the "Generar borrador"/confirm click). The card switches to its status
   * phase and keys on this — `WorkflowCard.vue` treats any truthy `runId` as "a run is executing,"
   * so this must stay unset until the user actually confirms. The run negotiated BEFORE confirm
   * lives in `draftRunId` instead, precisely so it never trips that check.
   */
  runId?: string
  /**
   * The run whose document this negotiation regenerates in place on every later edit (MAR-437
   * follow-up). Set by the first confirm, or on a card rebuilt from its run after a reload — never by
   * a proposal merely appearing (MAR-988): while it is unset, edits stay local and no `/draft` is
   * posted. Deliberately a DIFFERENT field from `runId`: `WorkflowCard.vue`'s "running" status view
   * keys on `runId` alone, and a rebuilt card is still an editable proposal.
   */
  draftRunId?: string | null
  /**
   * The persisted, workflow-linked document this negotiation regenerates in place (MAR-437
   * follow-up). It exists only once the user has confirmed (MAR-988). `null` while no document has
   * been produced (e.g. every input is still blank on a schema-less template with no deterministic fill).
   */
  itemId?: string | null
  /** Populated on a failed start, verbatim from the server so the reason is not re-invented. */
  error?: string
  /** Deliverables the finished run produced, for the side-by-side open. */
  outputs?: { id: string; name: string; documentId?: string | null }[]
  /**
   * How complete this run is allowed to be — THE run-level fact (MAR-437).
   *
   * `strict` refuses a document with placeholders left in it. `draft` says the placeholders are the
   * point: the user will finish it in Word, and substituting nothing at all is a valid outcome. It
   * relaxes COMPLETENESS only — a legally defective document is still refused.
   *
   * Sent as a typed top-level field on `POST /api/workflows/:id/run`. It replaces the `__draft: true`
   * key that used to be smuggled inside `inputValues`, which made run intent indistinguishable from
   * a workflow input that happened to be called `__draft`.
   */
  completeness?: RunCompleteness
  /**
   * Required inputs still blank when the run started — what "borrador con N espacios" counts.
   *
   * Frozen at launch on purpose. The fields stay editable on screen, and a count that moved after
   * the run started would describe a proposal nobody ran.
   */
  blanks?: number
  /**
   * The message this negotiation's card belongs to (MAR-437 follow-up) — needed to call `/draft`.
   * Set once, when the card is built (a fresh proposal or a rehydrated one); never reassigned.
   */
  conversationMessageId?: string | null
  /**
   * The ADK call id of the `run_workflow` call this proposal came from (MAR-988). Its persisted
   * `tool_calls` row is where an UNCONFIRMED proposal's edits and a Cancelar are saved
   * (`PUT /api/conversations/:id/workflow-proposals/:callId`), since no run exists before the
   * confirm. Deliberately not the card's `callId`: that one resumes the paused session, and a
   * rebuilt card must never resume it again.
   */
  proposalCallId?: string
  /**
   * The card was rebuilt from its persisted call on load, not from a run. If the proposal turns out
   * to have a run (it was confirmed), `rehydrateWorkflowCards` replaces it with the run's card.
   */
  restoredFromCall?: boolean
}

/** A `WorkflowRequestField`, minus what only a live card needs (`value`/`prefilled`). */
export interface WorkflowFieldMeta {
  key: string
  label: string
  type: string
  required: boolean
  options?: { value: string; label: string }[]
  constraints?: any
}

/**
 * What actually happened after the user confirmed a storage mutation (MAR-957). Unset while the
 * effect is still running, which is why the card must not read `status === 'confirmed'` as success:
 * that status is set before the effect runs, so it says only that the user said yes.
 */
export interface StorageMutationOutcome {
  ok: boolean
  /** Delete only: how many of the requested items were actually removed / could not be. */
  succeeded?: number
  failed?: number
}

export interface DeleteConfirmRequest {
  items: ConfirmableItem[]
  status: 'pending' | 'confirmed' | 'cancelled'
  outcome?: StorageMutationOutcome
}

export interface MoveConfirmRequest {
  items: ConfirmableItem[]
  destination: string
  status: 'pending' | 'confirmed' | 'cancelled'
  outcome?: StorageMutationOutcome
}

/** A new folder the agent proposes (MAR-988) — it used to be created before any card. */
export interface CreateFolderRequest {
  name: string
  /** Parent folder path; `/` is the library root. */
  path: string
  status: 'pending' | 'confirmed' | 'cancelled'
  outcome?: StorageMutationOutcome
}

/**
 * Documents the agent proposes to add to a case (MAR-988). Filing LINKS them — a `case_documents`
 * row each — and the files stay where they are in the library.
 *
 * `caseName` is RESOLVED FROM THE APP against `caseId`, never taken from the model — the same rule as
 * `ClearScopeRequest.scopeLabel` and `ConvertDocumentRequest.documentName`: the link is written against
 * the id, so a card naming one case while the id points at another is a click into the wrong case.
 */
export interface FileToCaseRequest {
  items: ConfirmableItem[]
  caseId: string
  caseName: string
  status: 'pending' | 'confirmed' | 'cancelled'
  /** True until `caseName` is resolved; the card cannot be accepted meanwhile. */
  loading?: boolean
  /** Set when `caseId` does not resolve to a case this org can see; the card cannot be accepted. */
  error?: string
  outcome?: StorageMutationOutcome
}

/**
 * The agent asking the user to lift the chat's document scope (§6b). Scope is strict — the agent
 * can't widen it itself — so this card is the only path from "what you want is outside the scope"
 * to actually reaching it.
 */
export interface ClearScopeRequest {
  /** The scope being asked about, as shown in the composer (`Client › Case`). */
  scopeLabel: string
  /** The agent's justification — what it needs to reach and why the scope blocks it. */
  reason: string
  status: 'pending' | 'confirmed' | 'cancelled'
}

export interface RenameConfirmRequest {
  fileId: string
  currentName: string
  newName: string
  status: 'pending' | 'confirmed' | 'cancelled'
  error?: string
  outcome?: StorageMutationOutcome
}

export interface ConfirmableItem {
  id: string
  name: string
  type?: 'folder' | 'document' | string
}

export interface DownloadDocumentsRequest {
  items: ConfirmableItem[]
  reason: string
  downloaded: string[] // ids already downloaded — drives per-button "✓ Descargado" feedback
}

export interface ToolActivity {
  id: number | string
  label: string
  completed: boolean
  segmentIndex: number
  /** When a live activity started (`Date.now()`), for the live line's seconds counter (MAR-1057).
   *  Absent on an activity rebuilt from history. */
  startedAt?: number
  /** The function-call id of the tool call this live activity belongs to, so its `tool_done` and its
   *  query or research question are matched exactly (MAR-1057). */
  callId?: string
  queries?: string[]
  details?: string[]
  /** Which domain this activity belongs to (legal / workflow / documents / help / generic) — what
   *  `ToolActivityList` groups and labels a collapsed summary by, instead of guessing from
   *  `queries` alone (see `shared/web-chat/toolActivity.ts`). Optional only because a handful of
   *  activities (e.g. the raw `toolStatus` push before its matching tool call is known) are
   *  constructed before a tool name is available; they read as `'generic'`. */
  kind?: ToolActivityKind
  /** How many identical calls (same `kind`, same first query, run back-to-back) this one chip
   *  stands for — set only by `ToolActivityList`'s merge step, never at construction. `undefined`
   *  and `1` both mean "just this one"; the chip only shows a "×N" mark once it's above 1. */
  count?: number
}

/**
 * True when the message carries a card — i.e. it must render even with empty text.
 *
 * This used to compare against `MESSAGE_CARD_FIELDS`, a hand-maintained list of the eight card
 * fields, and the bug it guarded was real: `clear_chat_scope` shipped without being added to it, so
 * the agent proposed it with no prose, the message was filtered out, the card never rendered — and
 * because a confirm tool pauses the ADK runner until the browser answers, the whole conversation
 * hung with no error anywhere. `renameConfirmRequest` had the same latent gap.
 *
 * With one `card` field there is no list to forget, and that failure mode is gone by construction.
 */
export function messageCarriesCard(msg: WebChatMessage): boolean {
  return msg.card != null || (msg.earlierCards?.length ?? 0) > 0
}

/** Every card the message puts on screen, in the order the turn produced them (MAR-1017). */
export function messageCards(msg: WebChatMessage): MessageCard[] {
  return [...(msg.earlierCards ?? []), ...(msg.card ? [msg.card] : [])]
}

/**
 * Put `card` on the message as its card. A result list it displaces is kept on `earlierCards`
 * rather than dropped — the one rule both the live `tool` event and a reload follow (MAR-1017).
 * Any other displaced card is replaced, as before: one decision card per message.
 */
export function setMessageCard(msg: WebChatMessage, card: MessageCard): void {
  if (msg.card?.toolId === 'present_document_results') msg.earlierCards = [...(msg.earlierCards ?? []), msg.card]
  msg.card = card
}
